import classNames from 'classnames'
import { GetServerSidePropsContext } from 'next'
import Head from 'next/head'
import { Button } from 'react-bootstrap'
import reactStringReplace from 'react-string-replace'

import CSBIcon from '@/assets/csb.svg'
import Card from '@/components/Card/Card'
import CardLists from '@/components/CardLists/CardLists'
import MetaData from '@/components/Combo/MetaData/MetaData'
import PlayedWith from '@/components/Combo/MetaData/PlayedWith'
import MultiImageBanner from '@/components/Combo/MultiImageBanner/MultiImageBanner'
import SideScroller from '@/components/Combo/SideScroller/SideScroller'
import StepBlocks from '@/components/Combo/StepBlocks/StepBlocks'
import StyledList from '@/components/Combo/StyledList/StyledList'
import Container from '@/components/Container/Container'
import DefaultHead from '@/components/DefaultHead/DefaultHead'
import Icon from '@/components/Icon/Icon'
import Main from '@/components/Main/Main'
import Mana, { ManaType } from '@/components/Mana/Mana'
import OutboundLink from '@/components/OutboundLink/OutboundLink'
import Tabs from '@/components/Tabs/Tabs'
import { ComboData, GenericPageProps } from '@/types/page'
import { ZONE_TO_ICON_MAP } from '@/utils/combos'
import { COLORS } from '@/utils/constants'
import { serverSidePropsFor } from '@/utils/helpers.server'
import { artCrop } from '@/utils/images'

import styles from './[comboNumber].module.scss'

type Props = GenericPageProps & {
  data: { combo: ComboData }
}

const format = (input: string) =>
  reactStringReplace(input, /\{([^}]+)\}/, (match) => <Mana color={match as ManaType} key={match} />)

const CombosSubSubPage = ({ data }: Props) => {
  const banner = <MultiImageBanner images={data.combo.cardIds.map((id) => artCrop(id))} />
  const comboRecCards = (data.container.json_dict.cardlists || [])[0].cardviews

  return (
    <Main banner={banner}>
      <div className='d-flex justify-content-center'>
        <div className={styles.topOuter}>
          <div className={classNames(styles.topContainer)}>
            <SideScroller>
              {comboRecCards.map((card, i) => (
                <div className={styles.card} key={i}>
                  <Card card={card} scalable />
                </div>
              ))}
            </SideScroller>
          </div>
        </div>
      </div>
      <DefaultHead data={data} />
      <Head>
        <link href='https://fonts.googleapis.com' rel='preconnect' />
        <link href='https://fonts.gstatic.com' rel='preconnect' />
        {/* eslint-disable-next-line @next/next/no-page-custom-font */}
        <link href='https://fonts.googleapis.com/css2?family=League+Gothic&display=swap' rel='stylesheet' />
      </Head>
      <Container className='mb-2'>
        <PlayedWith cardLists={data.container.json_dict.cardlists?.slice(1)} combo={data.combo} />
        <StepBlocks
          steps={[
            {
              node: (
                <div className='p-3'>
                  <h2 className={styles.header} id='prereqInfo'>
                    <Icon icon='listCheck' />
                    &nbsp;&nbsp;&nbsp;Prerequisites
                  </h2>
                  <h5 className={styles.subHeader}>What do I need to start this combo?</h5>
                  <StyledList
                    groupIcon='check'
                    items={data.combo.prerequisites.map((prereq) => ({
                      icon:
                        prereq.zones.length > 0
                          ? ZONE_TO_ICON_MAP[prereq.zones[0] as keyof typeof ZONE_TO_ICON_MAP]
                          : undefined,
                      item: <span>{format(prereq.description)}</span>,
                    }))}
                  />
                </div>
              ),
            },
            {
              node: (
                <div className='p-3'>
                  <h2 className={styles.header}>
                    <Icon icon='route' />
                    &nbsp;&nbsp;&nbsp;Steps
                  </h2>
                  <h5 className={styles.subHeader}>How does this combo work?</h5>
                  <StyledList items={data.combo.steps.map((line) => format(line))} />
                </div>
              ),
            },
            {
              node: (
                <div className='p-3'>
                  <h2 className={styles.header} id='resultsInfo'>
                    <Icon icon='infinity' />
                    &nbsp;&nbsp;&nbsp;Results
                  </h2>
                  <h5 className={styles.subHeader}>What do I get from this combo?</h5>
                  <StyledList
                    groupIcon='star'
                    items={data.combo.results.map((result) => ({
                      icon: result.toLowerCase().includes('infinite') ? 'infinity' : 'star',
                      item: format(result),
                    }))}
                  />
                </div>
              ),
            },
          ]}
        />
        <MetaData combo={data.combo} />
        <div className='d-flex justify-content-center m-3 gap-3'>
          <OutboundLink url={`https://commanderspellbook.com/combo/${data.combo.combo_id}`}>
            <Button className={styles.csb} variant='outline'>
              <img alt='Spellbook Logo' height='20' src={CSBIcon.src} /> View on Commander Spellbook
            </Button>
          </OutboundLink>
          <OutboundLink url={`https://commanderspellbook.com/report-error/?comboId=${data.combo.combo_id}`}>
            <Button variant='outline-danger'>Report Error</Button>
          </OutboundLink>
        </div>
      </Container>
      <Tabs />
      <CardLists initCardLists={data.container.json_dict.cardlists?.slice(1)} />
    </Main>
  )
}

export const getServerSideProps = async (context: GetServerSidePropsContext) => {
  if (context.query.slug !== 'colorless' && COLORS.hasOwnProperty(context.query.slug as string)) {
    const colorName = COLORS[context.query.slug as keyof typeof COLORS].toLowerCase()
    return {
      redirect: {
        destination: `/combos/${colorName}/${context.query.comboNumber}`,
        permanent: true,
      },
    }
  }

  return serverSidePropsFor(
    `${process.env.NEXT_PUBLIC_EDHREC_JSON_URL}/pages/combos/${context.query.slug}/${context.query.comboNumber}.json`,
  )
}

export default CombosSubSubPage
